"use client"

import React from "react"
import cx from "classnames"
// Using native JavaScript sort instead of lodash
import OvertimeOutcomeButton from "@/compositions/OutcomeButton/OvertimeOutcomeButton"
import { getPositionTextV2 } from "@/Overtime/utils/marketsV2"
import { MarketType, isFuturesMarket } from "overtime-utils"
import type { SportMarket } from "@/Overtime/types/markets"
import {
  isSgpMarket,
  hasValidSgpCombinedPositions,
} from "@/Overtime/utils/sgpHelpers"

type ExtendedSportMarket = SportMarket & {
  title?: string
  isSgpMarket?: boolean
}

type OvertimeButtonsProps = {
  market: ExtendedSportMarket
  marketName: string
  game: any
  section?: string
}

const OvertimeButtons: React.FC<OvertimeButtonsProps> = ({
  market,
  marketName,
  game,
  section,
}) => {
  // Check if this is an SGP market - use the flag from parent or detect locally
  const isSgpMarketFlag = isSgpMarket(market)
  const hasCombinedPositions = hasValidSgpCombinedPositions(market)
  const customOrder = [0, 2, 1]
  // Create odds info with position and names, following the same pattern as OvertimeMarkets
  const oddsInfo = (
    marketName === "Winner"
      ? customOrder
      : (market?.odds?.map((_, i) => i) ?? [])
  ).map((i: number) => ({
    odd: market.odds[i],
    position: i,
    positionName: market.positionNames?.[i] ?? "",
  }))

  function isCombiningFuturesNotSupported(ticket: any[], newMarket: any) {
    if (ticket.length === 0) {
      return false
    }
    const ticketHasFutures = ticket.some(sel => isFuturesMarket(sel.typeId))
    const newIsFutures = isFuturesMarket(newMarket.typeId)
    return ticketHasFutures || newIsFutures
  }

  const isFutures = isFuturesMarket(market.typeId)
  const isGameStarted = !market.live && market.maturityDate < new Date()
  const isGameOpen = market.isOpen && !isGameStarted

  return (
    <div className="w-full">
      <div
        className={cx(
          "grid gap-x-2 gap-y-3 w-full mt-2 first-of-type:mt-0",
          isSgpMarketFlag || oddsInfo.length == 1
            ? "grid-cols-1" // SGP markets or markets with 2 or fewer odds: 1 button per row
            : oddsInfo.length === 3
              ? "grid-cols-3"
              : "grid-cols-2"
        )}
      >
        {oddsInfo.map((outcome, index) => {
          const key = outcome.position
          const positionText = getPositionTextV2(market, outcome.position, true)

          const isZeroOdd =
            !outcome.odd ||
            outcome.odd == 0 ||
            market.typeId === MarketType.EMPTY

          // Transform market data for futures and SGP markets
          const transformedMarket = {
            ...market,
            marketName: isFutures ? positionText : marketName,
            displayPosition: outcome.position,
            // Ensure SGP markets have the proper structure
            ...(isSgpMarketFlag &&
              hasCombinedPositions && {
                combinedPositions: market.combinedPositions,
                type: market.type,
                positionNames: market.positionNames,
                displayPosition: outcome.position, // Ensure displayPosition is set for SGP markets
              }),
          }

          // For SGP markets, use the position name from positionNames array
          const finalMarketName =
            isSgpMarketFlag &&
            market.positionNames &&
            market.positionNames[outcome.position]
              ? market.positionNames[outcome.position]
              : isFutures
                ? market.title || ""
                : marketName

          // Ensure fullMarket also has the combinedPositions structure for SGP markets
          const enhancedFullMarket = {
            ...market,
            ...(isSgpMarketFlag &&
              hasCombinedPositions && {
                combinedPositions: market.combinedPositions,
                type: market.type,
                positionNames: market.positionNames,
              }),
          }

          return (
            <OvertimeOutcomeButton
              key={key}
              marketName={finalMarketName}
              outcome={outcome}
              positionText={positionText}
              playerProps={market.playerProps}
              isZeroOdd={isZeroOdd}
              market={transformedMarket}
              game={game}
              section={section}
              size={40}
              isGameOpen={isGameOpen}
              fullMarket={enhancedFullMarket}
              isCombiningFuturesNotSupported={isCombiningFuturesNotSupported} // Pass function as prop
            />
          )
        })}
      </div>
    </div>
  )
}

export const OvertimeMarketSkeleton: React.FC = () => {
  return (
    <div className="w-full mt-2 first-of-type:mt-0">
      <div className="bone w-[110px] h-[0.875rem] ds:mx-auto !block rounded-md mb-[0.375rem]" />
      <div className="grid gap-x-2 gap-y-3 grid-cols-2 w-full">
        <div className="bone w-full h-7 rounded-min" />
        <div className="bone w-full h-7 rounded-min" />
      </div>
    </div>
  )
}

export type OvertimeMarketProps = {
  childMarkets: ExtendedSportMarket
  game: any
  section?: string
  marketName?: string
}

const OvertimeMarket: React.FC<OvertimeMarketProps> = ({
  childMarkets,
  game,
  section,
  marketName,
}) => {
  const isFutures = isFuturesMarket(childMarkets.typeId)
  return (
    <div className="w-full mt-2 first-of-type:mt-0 ">
      <div className="mb-[0.375rem] mt-auto text-caption-12 font-medium text-accent-grey-200 ds:text-center">
        {isFutures ? childMarkets.title || "" : marketName}
      </div>
      <OvertimeButtons
        market={childMarkets}
        marketName={marketName as string}
        game={game}
        section={section}
      />
    </div>
  )
}

export default OvertimeMarket
